import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faPlus, faEdit, faBan, faSave, faSpinner, faSearch,
  faCheckCircle, faExclamationTriangle, faBalanceScale, faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { formatKg, formatMetres, metresForKg, kgPerMetre } from '../../utils/coilMath';
import coilService from '../../services/coilService';
import ConfirmDialog from '../common/ConfirmDialog';
import SearchableSelect from '../common/SearchableSelect';
import CoilEntrySheet from './CoilEntrySheet';
import LooseCoilBadge from './LooseCoilBadge';

/**
 * CoilLedgerDrawer
 *
 * The subsidiary ledger for one product size in one warehouse: every physical coil, what
 * is left of it and what state it is in. Coil stock starts empty, so this is also where
 * coils first come into existence — a purchase receipt is the other door in.
 *
 * The reconciliation banner is not decoration. A subsidiary ledger nobody can check
 * against the control account is one nobody trusts, and the moment the coil weights and
 * the stock balance disagree the picking screens are quietly lying. So the comparison
 * sits at the top, always, and is re-read after every mutation.
 *
 * Props:
 * - isOpen, onClose: drawer visibility
 * - warehouseId, productSizeId: the ledger's scope; both are required to load anything
 * - productName: shown in the header
 */

// Status pill colours. "Whole" is deliberately INFO rather than SUCCESS so the only
// green on screen is the reconciliation tick — that is the signal people scan for.
const STATUS_META = {
  whole: { labelKey: 'coils.ledger.status.whole', color: UI_COLORS.STATUS.INFO },
  partly_consumed: { labelKey: 'coils.ledger.status.partlyConsumed', color: UI_COLORS.STATUS.WARNING },
  exhausted: { labelKey: 'coils.ledger.status.exhausted', color: UI_COLORS.TEXT.MUTED },
  written_off: { labelKey: 'coils.ledger.status.writtenOff', color: UI_COLORS.STATUS.ERROR },
};

const EMPTY_FILTERS = { colour: '', batch: '', includeInactive: false };

/** One label/value pair inside a coil card. */
const CoilField = ({ label, value, emphasis = false }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>{label}</div>
      <div className={`text-sm ${emphasis ? 'font-bold' : 'font-medium'}`} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
        {value || t('common.symbols.emDash')}
      </div>
    </div>
  );
};

const CoilLedgerDrawer = ({ isOpen, onClose, warehouseId, warehouseName, productSizeId, productName }) => {
  const { t } = useTranslation();

  const [coils, setCoils] = useState([]);
  const [totals, setTotals] = useState({ count: 0, remainingKg: 0 });
  const [recon, setRecon] = useState(null);
  const [reconFailed, setReconFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [batchInput, setBatchInput] = useState('');
  const [colourOptions, setColourOptions] = useState([]);

  const [entryOpen, setEntryOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ batch_number: '', supplier_batch_ref: '', colour: '', received_date: '', remark: '' });
  const [adjustingId, setAdjustingId] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ remaining_weight_kg: '', note: '' });
  const [adjustError, setAdjustError] = useState(null);
  const [writeOffTarget, setWriteOffTarget] = useState(null);
  const [writeOffReason, setWriteOffReason] = useState('');
  const [writeOffError, setWriteOffError] = useState(null);

  // Batch typing is debounced so a six-character batch number is one request, not six.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => (prev.batch === batchInput ? prev : { ...prev, batch: batchInput }));
    }, 350);
    return () => clearTimeout(timer);
  }, [batchInput]);

  /**
   * Load the list and the reconciliation together.
   *
   * They are always read as a pair: a manual add CREDITS the warehouse stock balance
   * server-side, an adjustment or a write-off debits it, so the banner is stale the
   * instant the list changes. Every mutation below ends in refresh().
   */
  const refresh = useCallback(async () => {
    if (!isOpen || !warehouseId || !productSizeId) return;

    setLoading(true);
    // Every exit resets the flag. A throw here used to leave the drawer stuck on its
    // skeleton with no way out but a page refresh.
    try {
    const [listRes, reconRes] = await Promise.all([
      coilService.getCoils({
        warehouse_id: warehouseId,
        product_size_id: productSizeId,
        colour: filters.colour || undefined,
        batch: filters.batch || undefined,
        include_inactive: filters.includeInactive,
        limit: 200,
      }),
      coilService.getReconciliation({ warehouse_id: warehouseId, product_size_id: productSizeId }),
    ]);

    if (listRes.success) {
      const items = listRes.data?.items || [];
      setCoils(items);
      setTotals({
        count: listRes.data?.total ?? items.length,
        remainingKg: listRes.data?.total_remaining_kg ?? 0,
      });
      setError(null);
      // Only rebuild the colour list from an unfiltered read — a filtered one contains
      // just the colour that is already selected, which would strip the other options.
      if (!filters.colour && !filters.batch) {
        setColourOptions([...new Set(items.map((coil) => coil.colour).filter(Boolean))].sort());
      }
    } else {
      setError(listRes.error || t('coils.ledger.messages.loadFailed'));
      setCoils([]);
      setTotals({ count: 0, remainingKg: 0 });
    }

    if (reconRes.success) {
      const rows = Array.isArray(reconRes.data) ? reconRes.data : [];
      const match = rows.find((row) => (
        Number(row.product_size_id) === Number(productSizeId)
        && Number(row.warehouse_id) === Number(warehouseId)
      ));
      setRecon(match || rows[0] || null);
      setReconFailed(false);
    } else {
      setRecon(null);
      setReconFailed(true);
    }

    } catch (err) {
      setError(t('coils.ledger.messages.loadFailed'));
      setCoils([]);
      setTotals({ count: 0, remainingKg: 0 });
      setRecon(null);
      setReconFailed(true);
    } finally {
      setLoading(false);
    }
  }, [isOpen, warehouseId, productSizeId, filters.colour, filters.batch, filters.includeInactive, t]);

  useEffect(() => { refresh(); }, [refresh]);

  // Reset the transient UI whenever the drawer opens, without churning `filters`
  // identity when they are already clean (that would fire a second load).
  useEffect(() => {
    if (!isOpen) return;
    setBatchInput('');
    setFilters((prev) => (prev.colour || prev.batch || prev.includeInactive ? EMPTY_FILTERS : prev));
    setEditingId(null);
    setAdjustingId(null);
    setWriteOffTarget(null);
    setEntryOpen(false);
    setNotice(null);
    setError(null);
  }, [isOpen]);

  const formatDate = (value) => {
    if (!value) return t('common.symbols.emDash');
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  /** Metres left on a coil — the server's figure, re-derived from kg/m if it is absent. */
  const remainingMetres = (coil) => (
    coil.remaining_length_m !== undefined && coil.remaining_length_m !== null
      ? Number(coil.remaining_length_m)
      : metresForKg(coil, coil.remaining_weight_kg)
  );

  /** A coil that can still be adjusted or written off. */
  const isLive = (coil) => coil.is_active !== false && coil.status !== 'written_off';

  const colourSelectOptions = useMemo(() => ([
    { value: '', label: t('coils.picker.filters.allColours') },
    ...colourOptions.map((colour) => ({ value: colour, label: colour })),
  ]), [colourOptions, t]);

  // --- Mutations ------------------------------------------------------------------

  /**
   * Persist the coils captured in the entry sheet, one at a time.
   *
   * Sequential on purpose: a failure part-way should stop rather than fire the rest.
   * Returning false keeps the sheet open with the operator's typing; once anything HAS
   * been created we let it close instead, because re-submitting the same sheet would
   * duplicate the coils that already landed — and the refreshed list below shows
   * exactly which those are.
   */
  const handleEntrySave = async (newCoils) => {
    setBusy(true);
    setNotice(null);
    setError(null);

    let created = 0;
    let failure = null;
    try {
      for (const coil of newCoils) {
        const res = await coilService.createCoil({
          warehouse_id: warehouseId,
          product_size_id: productSizeId,
          ...coil,
        });
        if (!res.success) {
          failure = res.error;
          break;
        }
        created += 1;
      }

      if (created > 0) await refresh();
    } catch (err) {
      // Whatever landed before the throw is real and already in stock, so `created`
      // stands and the caller is still told what failed.
      failure = err?.message || t('coils.ledger.messages.loadFailed');
    } finally {
      setBusy(false);
    }

    if (failure) {
      setError(failure);
      return created === 0 ? false : undefined;
    }

    setNotice(t('coils.ledger.messages.created', { count: created }));
    return undefined;
  };

  const openEdit = (coil) => {
    setAdjustingId(null);
    setEditingId(coil.id);
    setEditForm({
      batch_number: coil.batch_number || '',
      supplier_batch_ref: coil.supplier_batch_ref || '',
      colour: coil.colour || '',
      received_date: coil.received_date || '',
      remark: coil.remark || '',
    });
  };

  const handleEditSave = async () => {
    if (!String(editForm.batch_number).trim()) {
      setError(t('coils.errors.batchRequired'));
      return;
    }

    setBusy(true);
    setNotice(null);
    let res;
    try {
      res = await coilService.updateCoil(editingId, {
        batch_number: String(editForm.batch_number).trim(),
        supplier_batch_ref: String(editForm.supplier_batch_ref).trim() || null,
        colour: String(editForm.colour).trim() || null,
        received_date: editForm.received_date || null,
        remark: String(editForm.remark).trim() || null,
      });
    } catch (err) {
      res = { success: false, error: err?.message || t('coils.ledger.messages.loadFailed') };
    } finally {
      setBusy(false);
    }

    if (!res.success) {
      setError(res.error);
      return;
    }
    setError(null);
    setEditingId(null);
    setNotice(t('coils.ledger.messages.updated'));
    await refresh();
  };

  const openAdjust = (coil) => {
    setEditingId(null);
    setAdjustError(null);
    setAdjustingId(coil.id);
    setAdjustForm({ remaining_weight_kg: formatKg(coil.remaining_weight_kg), note: '' });
  };

  const handleAdjustSave = async () => {
    const text = String(adjustForm.remaining_weight_kg).trim();
    if (!text) {
      setAdjustError(t('coils.errors.weightRequired'));
      return;
    }
    // Whole kilograms, enforced by the database column. Zero is legal — that is a coil
    // that has been used up.
    if (!/^\d+$/.test(text)) {
      setAdjustError(t('coils.errors.wholeKgRequired'));
      return;
    }

    setBusy(true);
    setNotice(null);
    let res;
    try {
      res = await coilService.adjustCoilWeight(adjustingId, {
        remaining_weight_kg: parseInt(text, 10),
        note: String(adjustForm.note).trim() || undefined,
      });
    } catch (err) {
      res = { success: false, error: err?.message || t('coils.ledger.messages.loadFailed') };
    } finally {
      setBusy(false);
    }

    if (!res.success) {
      setAdjustError(res.error);
      return;
    }
    setAdjustError(null);
    setAdjustingId(null);
    setNotice(t('coils.ledger.messages.adjusted'));
    await refresh();
  };

  const handleWriteOff = async () => {
    if (!String(writeOffReason).trim()) {
      setWriteOffError(t('coils.errors.reasonRequired'));
      return;
    }

    setBusy(true);
    setNotice(null);
    let res;
    try {
      res = await coilService.writeOffCoil(writeOffTarget.id, { reason: String(writeOffReason).trim() });
    } catch (err) {
      res = { success: false, error: err?.message || t('coils.ledger.messages.loadFailed') };
    } finally {
      setBusy(false);
    }

    if (!res.success) {
      setWriteOffError(res.error);
      return;
    }
    setWriteOffTarget(null);
    setWriteOffReason('');
    setWriteOffError(null);
    setNotice(t('coils.ledger.messages.writtenOff'));
    await refresh();
  };

  if (!isOpen) return null;

  // --- Reconciliation banner ------------------------------------------------------

  const matched = Boolean(recon?.matched);
  const differenceKg = Math.round(Number(recon?.difference_kg) || 0);
  const signedDifference = `${differenceKg > 0 ? '+' : differenceKg < 0 ? '-' : ''}${formatKg(Math.abs(differenceKg))}`;
  const bannerColor = reconFailed || !recon
    ? UI_COLORS.TEXT.MUTED
    : (matched ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR);

  const inputClass = 'w-full px-3 py-2 rounded-lg border-2 text-sm outline-none';
  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };
  const labelClass = 'block text-xs font-semibold mb-1';

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex justify-end"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="h-full w-full max-w-4xl flex flex-col shadow-2xl"
          style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
          onClick={(event) => event.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <div>
              <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('coils.ledger.title')}
              </h2>
              {productName && (
                <p className="text-sm mt-1 font-medium" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {productName}
                  {warehouseName ? ` — ${warehouseName}` : ''}
                </p>
              )}
              <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('coils.ledger.subtitle')}</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setEntryOpen(true)}
                disabled={busy || !warehouseId || !productSizeId}
                className="px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center transition-all duration-200 disabled:opacity-60"
                style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t('coils.ledger.addCoil')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-110"
                style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
                aria-label={t('common.buttons.close')}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {/* Reconciliation banner — the control-account check */}
          <div className="px-6 pt-4">
            <div
              className="rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3 border"
              style={{
                backgroundColor: colorHelpers.withOpacity(bannerColor, 0.08),
                borderColor: colorHelpers.withOpacity(bannerColor, 0.35),
              }}
            >
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon={matched && !reconFailed && recon ? faCheckCircle : faExclamationTriangle}
                  style={{ color: bannerColor }}
                />
                <span className="text-sm font-bold" style={{ color: bannerColor }}>
                  {reconFailed || !recon
                    ? t('coils.ledger.reconcile.unavailable')
                    : (matched
                      ? t('coils.ledger.reconcile.matched')
                      : t('coils.ledger.reconcile.mismatch', { difference: signedDifference }))}
                </span>
              </div>

              {recon && !reconFailed && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                  <span className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {t('coils.ledger.reconcile.coilTotal')}
                    {': '}
                    <strong style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {formatKg(recon.coil_total_kg)} {t('common.units.kg')}
                    </strong>
                  </span>
                  <span className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {t('coils.ledger.reconcile.inventoryTotal')}
                    {': '}
                    <strong style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {formatKg(recon.stock_quantity)} {t('common.units.kg')}
                    </strong>
                  </span>
                  <span className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    {t('coils.ledger.reconcile.coilCount', { count: recon.coil_count ?? 0 })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 pt-4 pb-3 flex flex-wrap items-end gap-3">
            <div className="w-48">
              <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.picker.filters.colour')}</label>
              <SearchableSelect
                name="colour"
                value={filters.colour}
                onChange={(event) => setFilters((prev) => ({ ...prev, colour: event.target.value }))}
                options={colourSelectOptions}
                placeholder={t('coils.picker.filters.allColours')}
              />
            </div>

            <div className="w-56">
              <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.picker.filters.batch')}</label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-xs"
                  style={{ color: UI_COLORS.TEXT.MUTED }}
                />
                <input
                  type="text"
                  value={batchInput}
                  onChange={(event) => setBatchInput(event.target.value)}
                  className={`${inputClass} pl-8`}
                  style={inputStyle}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              <input
                type="checkbox"
                checked={filters.includeInactive}
                onChange={(event) => setFilters((prev) => ({ ...prev, includeInactive: event.target.checked }))}
                className="h-4 w-4 rounded"
                style={{ accentColor: UI_COLORS.BRAND.TERTIARY }}
              />
              {t('coils.ledger.filters.includeInactive')}
            </label>

            {loading && (
              <span className="text-xs pb-2 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            )}
          </div>

          {/* Messages */}
          {(error || notice) && (
            <div className="px-6 pb-2">
              <div
                className="p-3 text-sm rounded-xl"
                style={{
                  backgroundColor: colorHelpers.withOpacity(error ? UI_COLORS.STATUS.ERROR : UI_COLORS.STATUS.SUCCESS, 0.1),
                  color: error ? UI_COLORS.STATUS.ERROR : UI_COLORS.STATUS.SUCCESS,
                }}
              >
                {error || notice}
              </div>
            </div>
          )}

          {/* Coil list */}
          <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3">
            {!loading && coils.length === 0 && (
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faLayerGroup} size="2x" style={{ color: UI_COLORS.TEXT.MUTED }} />
                <p className="mt-3 font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('coils.ledger.empty.title')}</p>
                <p className="mt-1 text-sm max-w-md mx-auto" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('coils.ledger.empty.subtitle')}</p>
              </div>
            )}

            {coils.map((coil) => {
              const meta = STATUS_META[coil.status] || STATUS_META.whole;
              const live = isLive(coil);

              return (
                <div
                  key={coil.id}
                  className="rounded-xl border p-4"
                  style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.SECTION }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{coil.batch_number}</span>
                      <LooseCoilBadge coil={coil} />
                      <span
                        className="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide"
                        style={{ backgroundColor: colorHelpers.withOpacity(meta.color, 0.12), color: meta.color }}
                      >
                        {t(meta.labelKey)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => (editingId === coil.id ? setEditingId(null) : openEdit(coil))}
                        disabled={busy}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 disabled:opacity-60"
                        style={{ color: UI_COLORS.TEXT.SECONDARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1) }}
                      >
                        <FontAwesomeIcon icon={faEdit} className="mr-1.5" />
                        {t('coils.ledger.editCoil')}
                      </button>

                      {live && (
                        <button
                          type="button"
                          onClick={() => (adjustingId === coil.id ? setAdjustingId(null) : openAdjust(coil))}
                          disabled={busy}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 disabled:opacity-60"
                          style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.12) }}
                        >
                          <FontAwesomeIcon icon={faBalanceScale} className="mr-1.5" />
                          {t('coils.ledger.adjustWeight')}
                        </button>
                      )}

                      {live && Number(coil.remaining_weight_kg) > 0 && (
                        <button
                          type="button"
                          onClick={() => { setWriteOffTarget(coil); setWriteOffReason(''); setWriteOffError(null); }}
                          disabled={busy}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 disabled:opacity-60"
                          style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.12) }}
                        >
                          <FontAwesomeIcon icon={faBan} className="mr-1.5" />
                          {t('coils.ledger.writeOff')}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <CoilField
                      label={t('coils.picker.columns.remainingKg')}
                      value={`${formatKg(coil.remaining_weight_kg)} ${t('common.units.kg')}`}
                      emphasis
                    />
                    <CoilField
                      label={t('coils.picker.columns.remainingM')}
                      value={`${formatMetres(remainingMetres(coil))} ${t('common.units.m')}`}
                    />
                    <CoilField
                      label={t('coils.ledger.columns.heldKg')}
                      value={`${formatKg(coil.held_weight_kg || 0)} ${t('common.units.kg')}`}
                    />
                    {/* kg/m at full precision: a coil is often well under 1 kg per metre,
                        and this is the divisor behind every metre figure downstream. */}
                    <CoilField
                      label={t('coils.picker.columns.kgPerMetre')}
                      value={kgPerMetre(coil) > 0 ? kgPerMetre(coil).toFixed(3) : ''}
                    />
                    <CoilField label={t('coils.picker.columns.supplierRef')} value={coil.supplier_batch_ref} />
                    <CoilField label={t('coils.picker.columns.colour')} value={coil.colour} />
                    <CoilField label={t('coils.picker.columns.received')} value={formatDate(coil.received_date)} />
                    <CoilField
                      label={t('coils.picker.columns.availableKg')}
                      value={`${formatKg(coil.available_weight_kg || 0)} ${t('common.units.kg')}`}
                    />
                  </div>

                  {/* Descriptive edit — weight never moves through here, it goes through
                      adjust-weight so the change stays auditable. */}
                  {editingId === coil.id && (
                    <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-3 gap-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <div>
                        <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                          {t('coils.entry.fields.batchNumber')} {t('common.symbols.required')}
                        </label>
                        <input
                          type="text"
                          value={editForm.batch_number}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, batch_number: event.target.value }))}
                          className={inputClass}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.entry.fields.supplierBatchRef')}</label>
                        <input
                          type="text"
                          value={editForm.supplier_batch_ref}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, supplier_batch_ref: event.target.value }))}
                          className={inputClass}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.entry.fields.colour')}</label>
                        <input
                          type="text"
                          value={editForm.colour}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, colour: event.target.value }))}
                          className={inputClass}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.entry.fields.receivedDate')}</label>
                        <input
                          type="date"
                          value={editForm.received_date}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, received_date: event.target.value }))}
                          className={inputClass}
                          style={inputStyle}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.entry.fields.remark')}</label>
                        <input
                          type="text"
                          value={editForm.remark}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, remark: event.target.value }))}
                          className={inputClass}
                          style={inputStyle}
                        />
                      </div>
                      <div className="col-span-2 md:col-span-3 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          disabled={busy}
                          className="px-4 py-2 rounded-xl text-sm font-medium"
                          style={{ backgroundColor: 'transparent', color: UI_COLORS.TEXT.MUTED, border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}` }}
                        >
                          {t('common.buttons.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleEditSave}
                          disabled={busy}
                          className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center disabled:opacity-60"
                          style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
                        >
                          <FontAwesomeIcon icon={busy ? faSpinner : faSave} spin={busy} className="mr-2" />
                          {t('common.buttons.save')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Weight correction — stock take or weighbridge re-check */}
                  {adjustingId === coil.id && (
                    <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-3 gap-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <div>
                        <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                          {t('coils.ledger.newRemaining')} {t('common.symbols.required')}
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          step="1"
                          min="0"
                          value={adjustForm.remaining_weight_kg}
                          onChange={(event) => { setAdjustForm((prev) => ({ ...prev, remaining_weight_kg: event.target.value })); setAdjustError(null); }}
                          className={inputClass}
                          style={{ ...inputStyle, borderColor: adjustError ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.ledger.adjustNote')}</label>
                        <input
                          type="text"
                          value={adjustForm.note}
                          onChange={(event) => setAdjustForm((prev) => ({ ...prev, note: event.target.value }))}
                          placeholder={t('coils.ledger.adjustNotePlaceholder')}
                          className={inputClass}
                          style={inputStyle}
                        />
                      </div>
                      {adjustError && (
                        <div className="col-span-2 md:col-span-3 text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{adjustError}</div>
                      )}
                      <div className="col-span-2 md:col-span-3 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => { setAdjustingId(null); setAdjustError(null); }}
                          disabled={busy}
                          className="px-4 py-2 rounded-xl text-sm font-medium"
                          style={{ backgroundColor: 'transparent', color: UI_COLORS.TEXT.MUTED, border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}` }}
                        >
                          {t('common.buttons.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleAdjustSave}
                          disabled={busy}
                          className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center disabled:opacity-60"
                          style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
                        >
                          <FontAwesomeIcon icon={busy ? faSpinner : faSave} spin={busy} className="mr-2" />
                          {t('common.buttons.save')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer totals */}
          <div className="px-6 py-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <span className="text-sm font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {t('coils.ledger.totals', { count: totals.count, weight: formatKg(totals.remainingKg) })}
            </span>
          </div>
        </div>
      </div>

      {/* Manual entry. Each coil created here CREDITS the warehouse stock balance
          server-side, which is why handleEntrySave ends in refresh() — the banner above
          would otherwise still show the pre-add comparison. */}
      <CoilEntrySheet
        isOpen={entryOpen}
        onClose={() => setEntryOpen(false)}
        initialCoils={[]}
        productName={productName}
        unitLabel={t('common.units.kg')}
        onSave={handleEntrySave}
      />

      <ConfirmDialog
        isOpen={Boolean(writeOffTarget)}
        title={t('coils.ledger.writeOffTitle')}
        message={writeOffTarget ? (
          <div className="space-y-2">
            <p>
              {t('coils.ledger.writeOffConfirm', {
                batch: writeOffTarget.batch_number,
                weight: formatKg(writeOffTarget.remaining_weight_kg),
              })}
            </p>
            <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
              {t('coils.ledger.writeOffReason')} {t('common.symbols.required')}
            </label>
            <textarea
              value={writeOffReason}
              onChange={(event) => { setWriteOffReason(event.target.value); setWriteOffError(null); }}
              placeholder={t('coils.ledger.writeOffReasonPlaceholder')}
              className="w-full px-3 py-2 rounded-lg border-2 text-sm resize-none h-20 outline-none"
              style={{ ...inputStyle, borderColor: writeOffError ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
            />
            {writeOffError && <div className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{writeOffError}</div>}
          </div>
        ) : null}
        confirmLabel={t('coils.ledger.writeOff')}
        cancelLabel={t('common.buttons.cancel')}
        loadingLabel={t('common.buttons.saving')}
        confirmVariant="danger"
        loading={busy}
        confirmDisabled={!String(writeOffReason).trim()}
        onConfirm={handleWriteOff}
        onCancel={() => { setWriteOffTarget(null); setWriteOffReason(''); setWriteOffError(null); }}
      />
    </>
  );
};

export default CoilLedgerDrawer;
